import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Rocket, ExternalLink, Droplets, ArrowLeftRight } from "lucide-react";
import { api } from "../lib/api";
import { fmtNum, short } from "../lib/format";
import { Panel, Empty } from "./State";
import { TokenLogo } from "./TokenSelect";
import { LaunchChecklist } from "./LaunchChecklist";

const VerifiedPools = ({ token }) => {
  const [d, setD] = useState(null);
  const [partial, setPartial] = useState(false);
  useEffect(() => { api.get(`/liquidity/pools/${token.address}`).then((r) => { setD(r.data.pools); setPartial(r.data.partial); }).catch(() => { setD([]); setPartial(true); }); }, [token.address]);
  if (d === null) return <p className="text-[11px] text-slate-500">Reading pools on-chain…</p>;
  if (partial && !d.some((p) => p.live)) return <p className="text-[11px] text-warning" data-testid={`creator-pools-error-${token.address}`}>Couldn't read every pool from the RPC. Refresh to try again.</p>;
  const live = d.filter((p) => p.live);
  if (!live.length) return <p className="text-[11px] text-slate-500" data-testid={`creator-pools-empty-${token.address}`}>No liquidity pools found on-chain yet.</p>;
  return (
    <ul className="space-y-1" data-testid={`creator-pools-${token.address}`}>
      {live.map((p) => (
        <li key={p.pool} className="flex flex-wrap items-center gap-2 text-[11px] font-mono">
          <span className="text-lime">{token.symbol}/{p.quote_symbol}</span><span className="text-slate-500">{p.protocol} · {p.fee / 10000}%</span>
          <span className="text-slate-300">{fmtNum(p.token_reserve_fmt, 2)} {token.symbol} + {fmtNum(p.quote_reserve_fmt, 6)} {p.quote_symbol}</span>
          <a href={`${token.explorer}/address/${p.pool}`} target="_blank" rel="noreferrer" className="text-slate-500 hover:text-lime">{short(p.pool)}</a>
        </li>))}
    </ul>
  );
};

export const LaunchedTokens = ({ creator, isMe }) => {
  const [list, setList] = useState(null);
  useEffect(() => { api.get("/launch/tokens", { params: { creator } }).then((r) => setList(r.data)).catch(() => setList([])); }, [creator]);
  return (
    <Panel title="Launched tokens" icon={Rocket} testid="profile-launched-tokens">
      {!list ? <p className="px-5 py-4 text-xs text-slate-500">Loading…</p> : !list.length ? <Empty title={isMe ? "Launch your first token from the Launch page" : "No tokens launched with HIVEVORA"} /> : (
        <ul className="divide-y divide-white/5">
          {list.map((t) => (
            <li key={`${t.chain_id}-${t.address}`} data-testid={`launched-${t.address}`} className="px-5 py-3 space-y-2">
              <div className="flex items-center gap-3">
                <TokenLogo t={{ symbol: t.symbol, address: t.chain_id === 4663 ? t.address : undefined, logo: t.logo }} size={32} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-white">{t.symbol} <span className="text-xs text-slate-500">{t.name}</span> {t.chain_id === 46630 && <span className="ml-1 rounded bg-warning/15 px-1 text-[9px] font-bold text-warning">TESTNET</span>}</p>
                  <p className="text-[11px] font-mono text-slate-500 truncate">{t.address}</p>
                </div>
                {t.chain_id === 4663 && <>
                  <Link to={`/token/${t.address}`} data-testid={`launched-swap-${t.address}`} className="flex items-center gap-1 text-xs text-lime hover:underline"><ArrowLeftRight className="h-3 w-3" /> Swap</Link>
                  {isMe && <Link to={`/liquidity?tab=add&token=${t.address}`} className="hidden sm:flex items-center gap-1 text-xs text-lime hover:underline"><Droplets className="h-3 w-3" /> Liquidity</Link>}
                </>}
                <a href={`${t.explorer}/address/${t.address}`} target="_blank" rel="noreferrer" className="text-slate-500 hover:text-lime"><ExternalLink className="h-4 w-4" /></a>
              </div>
              {t.chain_id === 4663 && <VerifiedPools token={t} />}
              {isMe && <LaunchChecklist token={t} compact />}
            </li>
          ))}
        </ul>)}
    </Panel>
  );
};
